import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CreatorSearch from '../components/CreatorSearch';
import supabase from '../lib/supabase';

// 2026-10-19 security fix: no client-side admin-password compare.
// PasswordGate now probes GET /api/admin/whoami with the typed value
// as X-Admin-Key; 200 → store + enter, 401/403 → wrong password, 503
// → admin auth not configured (dev mis-wire). The old
// CRA-env-backed ADMIN_PASSWORD const was removed from this file
// (it baked a plaintext password into the production bundle at
// build time).
const BACKEND_API = process.env.REACT_APP_API_URL || 'https://tribaltech-backend.onrender.com';
const PLAN_OPTIONS = ['trial', 'starter', 'pro', 'studio', 'clean_slate'];

function fmt(n) {
  if (n === null || n === undefined) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function timeSince(iso) {
  if (!iso) return 'Never';
  const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

const PLAN_PRICES = { starter: 29, pro: 59, clean_slate: 49 };
const PLAN_LABELS = { starter: 'Starter', pro: 'Pro', clean_slate: 'Clean Slate' };
const PLAN_COLORS = { starter: '#4A9EFF', pro: '#A78BFA', clean_slate: '#34D399' };

function Sparkline({ data, color = '#A78BFA', height = 40 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const w = 120;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  });
  const area = `M${pts[0]} L${pts.join(' L')} L${w},${height} L0,${height} Z`;
  return (
    <svg width={w} height={height} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sg-${color.replace('#','')})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function BarChart({ data, color = '#A78BFA' }) {
  if (!data || data.length === 0) return <p style={styles.empty}>No signup data yet</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            title={`${d.date}: ${d.count} signup${d.count !== 1 ? 's' : ''}`}
            style={{
              width: '100%',
              height: Math.max((d.count / max) * 64, d.count > 0 ? 4 : 0),
              background: d.count > 0 ? color : 'rgba(255,255,255,0.06)',
              borderRadius: '2px 2px 0 0',
              transition: 'height 0.4s ease',
            }}
          />
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, sparkData, color = '#A78BFA', loading }) {
  return (
    <div style={styles.statCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={styles.statLabel}>{label}</p>
          <p style={{ ...styles.statValue, color }}>
            {loading ? <span style={{ opacity: 0.3 }}>——</span> : fmt(value)}
          </p>
          {sub && <p style={styles.statSub}>{sub}</p>}
        </div>
        {sparkData && <Sparkline data={sparkData} color={color} />}
      </div>
    </div>
  );
}

function PasswordGate({ onAuth }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState(false);
  const [errMsg, setErrMsg] = useState('Incorrect password');
  const [busy, setBusy] = useState(false);
  // 2026-10-19: probe the backend instead of client-side compare. The
  // typed password is sent as X-Admin-Key on a lightweight GET
  // /api/admin/whoami; the backend's middleware does a constant-time
  // compare against process.env.ADMIN_SECRET_KEY. No password ever
  // enters the compiled bundle.
  async function attempt() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_API}/api/admin/whoami`, {
        method: 'GET',
        headers: { 'X-Admin-Key': pw },
      });
      if (r.status === 200) {
        sessionStorage.setItem('cp_admin_auth', '1');
        sessionStorage.setItem('cp_admin_key', pw);
        onAuth();
        return;
      }
      // 401 / 403 → wrong password. 503 → admin auth not configured on
      // the backend (dev mis-wire). 5xx otherwise → generic error.
      if (r.status === 503) setErrMsg('Admin auth not configured on server');
      else if (r.status >= 500) setErrMsg('Server error — try again');
      else setErrMsg('Incorrect password');
    } catch (e) {
      setErrMsg('Network error — try again');
    } finally {
      setBusy(false);
      setErr(true);
      setTimeout(() => setErr(false), 2000);
    }
  }
  return (
    <div style={styles.gate}>
      <div style={{ fontSize: 40, color: '#A78BFA' }}>⬡</div>
      <h1 style={{ fontSize: 18, color: '#fff', fontWeight: 600 }}>Creator Protect</h1>
      <p style={{ fontSize: 12, color: '#555', marginTop: -8 }}>Admin access only</p>
      <input
        type="password"
        placeholder="Admin password"
        value={pw}
        onChange={e => setPw(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && attempt()}
        style={{ ...styles.gateInput, borderColor: err ? '#EF4444' : 'rgba(255,255,255,0.12)' }}
        autoFocus
      />
      <button onClick={attempt} style={styles.gateBtn} disabled={busy}>{busy ? '…' : 'Enter'}</button>
      {err && <p style={{ fontSize: 12, color: '#EF4444' }}>{errMsg}</p>}
    </div>
  );
}



// Single row in the creators table — plan dropdown that saves on
// change + on-demand Stripe subscription lookup. Both calls use
// the admin password stored in sessionStorage as X-Admin-Key.
function CreatorRow({ creator, rowIndex, reload }) {
  const [plan, setPlan] = useState(creator.plan || 'trial');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  async function cancelTrial() {
    const ok = window.confirm(
      `Cancel ${creator.email || 'this creator'}'s trial?\n\n` +
      `• Any active Stripe trial/subscription will be cancelled immediately.\n` +
      `• Plan will be flagged as trial_canceled.\n` +
      `• They will be blocked from signing in again.\n` +
      `• Existing JWT sessions keep working until expiry (up to 7 days).`
    );
    if (!ok) return;
    setCancelling(true);
    try {
      const r = await fetch(`${BACKEND_API}/api/admin/users/${creator.id}/cancel-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '',
        },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Cancel failed');
      alert(data.note || 'Trial cancelled.');
      setPlan('trial_canceled');
      if (reload) reload();
    } catch (e) {
      alert('Cancel failed: ' + e.message);
    }
    setCancelling(false);
  }

  async function loadProfile() {
    if (profile) { setProfile(null); return; } // toggle
    setLoadingProfile(true);
    try {
      const r = await fetch(`${BACKEND_API}/api/admin/users/${creator.id}/profile`, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Profile load failed');
      setProfile(data);
    } catch (e) {
      alert('Profile load failed: ' + e.message);
    }
    setLoadingProfile(false);
  }

  async function runScan() {
    setScanning(true);
    setScanResult(null);
    try {
      const r = await fetch(`${BACKEND_API}/api/admin/users/${creator.id}/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '',
        },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Scan failed');
      setScanResult(data);
      // Reload profile if open so leak count updates
      if (profile) {
        setProfile(null);
        setTimeout(loadProfile, 200);
      }
    } catch (e) {
      alert('Scan failed: ' + e.message);
    }
    setScanning(false);
  }

  async function savePlan(newPlan) {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_API}/api/admin/users/${creator.id}/plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '',
        },
        body: JSON.stringify({ plan: newPlan }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Plan update failed');
      setPlan(newPlan);
      setSavedAt(Date.now());
      if (reload) reload();
    } catch (e) {
      alert('Plan update failed: ' + e.message);
      setPlan(creator.plan || 'trial'); // revert
    }
    setSaving(false);
  }

  async function checkStripe() {
    setLoadingStripe(true);
    try {
      const r = await fetch(`${BACKEND_API}/api/admin/users/${creator.id}/stripe-status`, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Stripe lookup failed');
      setStripe(data);
    } catch (e) {
      alert('Stripe lookup failed: ' + e.message);
    }
    setLoadingStripe(false);
  }

  const rowBg = rowIndex % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)';
  const stripeBadgeStyle = creator.stripe_customer_id
    ? { background: '#16a34a22', color: '#4ade80' }
    : { background: '#71717a22', color: '#a1a1aa' };

  return (
    <>
      <tr style={{ background: rowBg }}>
        <td style={{ ...(typeof styles !== 'undefined' ? styles.td : {}), padding: '8px 12px', fontSize: 12 }}>{creator.email || '—'}</td>
        <td style={{ padding: '8px 12px' }}>
          <select
            value={plan}
            disabled={saving}
            onChange={e => savePlan(e.target.value)}
            style={{
              background: '#1a1a2e', color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4,
              padding: '3px 6px', fontSize: 11,
            }}
          >
            {PLAN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {saving && <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>saving…</span>}
          {savedAt && !saving && Date.now() - savedAt < 3000 && (
            <span style={{ fontSize: 10, color: '#4ade80', marginLeft: 6 }}>✓ saved</span>
          )}
        </td>
        <td style={{ padding: '8px 12px' }}>
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, ...stripeBadgeStyle }}>
            {creator.stripe_customer_id ? 'customer' : 'none'}
          </span>
        </td>
        <td style={{ padding: '8px 12px', fontSize: 12 }}>{fmtDate(creator.created_at)}</td>
        <td style={{ padding: '8px 12px' }}>
          <div style={{ display:'flex', gap: 6 }}>
            <button
              onClick={loadProfile}
              disabled={loadingProfile}
              style={{
                background: 'transparent', color: '#60a5fa',
                border: '1px solid rgba(96,165,250,0.3)', borderRadius: 4,
                padding: '3px 10px', fontSize: 10, cursor: 'pointer',
              }}
            >
              {loadingProfile ? '…' : (profile ? 'Hide' : 'Profile')}
            </button>
            <button
              onClick={checkStripe}
              disabled={loadingStripe}
              style={{
                background: 'transparent', color: '#A78BFA',
                border: '1px solid rgba(167,139,250,0.3)', borderRadius: 4,
                padding: '3px 10px', fontSize: 10, cursor: 'pointer',
              }}
            >
              {loadingStripe ? '…' : 'Stripe'}
            </button>
          </div>
        </td>
      </tr>
      {profile && (
        <tr style={{ background: 'rgba(96,165,250,0.04)' }}>
          <td colSpan={5} style={{ padding: '14px 16px', fontSize: 11, color: '#aaa' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ color:'#888', marginBottom: 2 }}>Plan</div>
                <div style={{ color:'#fff', marginBottom: 10 }}>{profile.plan || '—'}</div>
                <div style={{ color:'#888', marginBottom: 2 }}>Leak count</div>
                <div style={{ color:'#fff', marginBottom: 10 }}>{profile.leak_count}</div>
                <div style={{ color:'#888', marginBottom: 2 }}>Last leak detected</div>
                <div style={{ color:'#fff' }}>
                  {profile.last_scan_at ? new Date(profile.last_scan_at).toLocaleString() : 'never'}
                </div>
              </div>
              <div>
                <div style={{ color:'#888', marginBottom: 2 }}>Usernames on file ({profile.usernames.length})</div>
                <div style={{ color:'#fff', marginBottom: 10 }}>
                  {profile.usernames.length > 0 ? profile.usernames.join(', ') : '—'}
                </div>
                <div style={{ color:'#888', marginBottom: 2 }}>Joined</div>
                <div style={{ color:'#fff', marginBottom: 10 }}>
                  {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={runScan}
                    disabled={scanning || profile.usernames.length === 0}
                    style={{
                      background: '#7c3aed', color: '#fff',
                      border: 'none', borderRadius: 4,
                      padding: '6px 14px', fontSize: 12, fontWeight: 600,
                      cursor: (scanning || profile.usernames.length === 0) ? 'not-allowed' : 'pointer',
                      opacity: (scanning || profile.usernames.length === 0) ? 0.6 : 1,
                    }}
                  >
                    {scanning ? 'Scanning… (30-60s)' : 'Run scan now'}
                  </button>
                  {plan !== 'trial_canceled' && (
                    <button
                      onClick={cancelTrial}
                      disabled={cancelling}
                      style={{
                        background: 'transparent', color: '#f87171',
                        border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4,
                        padding: '6px 14px', fontSize: 12, fontWeight: 600,
                        cursor: cancelling ? 'not-allowed' : 'pointer',
                      }}
                      title="Cancel Stripe trial + block future logins"
                    >
                      {cancelling ? 'Cancelling…' : 'Cancel trial'}
                    </button>
                  )}
                </div>
                {scanResult && (
                  <div style={{ marginTop: 10, padding: 10, background:'rgba(167,139,250,0.08)', borderRadius: 4, fontSize: 11 }}>
                    <div style={{ color:'#fff', fontWeight: 600, marginBottom: 4 }}>
                      ✓ {scanResult.new_leaks} new leak{scanResult.new_leaks === 1 ? '' : 's'} found
                    </div>
                    <div style={{ color:'#888' }}>
                      Total hits: {scanResult.total_hits}  ·  Already known: {scanResult.already_known}
                    </div>
                    <div style={{ color:'#666', fontSize: 10, marginTop: 4 }}>
                      Platforms: {(scanResult.platforms_scanned || []).join(', ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
      {stripe && (
        <tr style={{ background: 'rgba(167,139,250,0.04)' }}>
          <td colSpan={5} style={{ padding: '12px 16px', fontSize: 11, color: '#aaa' }}>
            <div style={{ marginBottom: 6 }}>
              <strong style={{ color: stripe.mismatch ? '#FB923C' : '#4ade80' }}>
                {stripe.mismatch ? '⚠ Mismatch' : '✓ In sync'}
              </strong>
              {' — '}{stripe.note}
            </div>
            <div>Supabase plan: <code>{stripe.supabase_plan || 'null'}</code></div>
            <div>Stripe customer: <code>{stripe.stripe_customer || 'none'}</code></div>
            {stripe.subscriptions.length > 0 ? (
              <div style={{ marginTop: 6 }}>
                Stripe subscriptions:
                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                  {stripe.subscriptions.map(s => (
                    <li key={s.id}>
                      <code>{s.status}</code> — {s.items.map(i => `$${(i.amount_cents/100).toFixed(2)}/${i.interval}`).join(', ')}
                      {s.cancel_at_period_end && <span style={{ color: '#FB923C' }}> (cancelling)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ) : <div style={{ marginTop: 6, color: '#888' }}>No Stripe subscriptions.</div>}
          </td>
        </tr>
      )}
    </>
  );
}

function AdminDashboard({ onSignOut }) {
  const [data, setData] = useState({ creators: [], leaks: [], watermarks: [], cleanSlate: [], cohorts: [] });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Direct supabase reads against creators / leak_detections /
      // watermark_jobs / clean_slate_jobs used to work with the
      // anon key. RLS on those tables now blocks anon reads, so we
      // fetch the same payload through the admin-key-gated backend
      // route instead.
      const r = await axios.get(`${BACKEND_API}/api/admin/overview`, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
      });
      setData({
        creators: r.data.creators || [],
        leaks: r.data.leaks || [],
        watermarks: r.data.watermarks || [],
        cleanSlate: r.data.cleanSlate || [],
        cohorts: [],
      });
      setLastRefresh(new Date());
    } catch (e) { console.error('Admin load error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalCreators = data.creators.length;
  const activeCreators = data.creators.filter(c => c.stripe_customer_id).length;
  const trialCreators = 0;
  const totalLeaks = data.leaks.length;
  const totalDMCAs = data.leaks.filter(l => l.dmca_filed_at).length;
  const totalVideos = data.watermarks.reduce((s, w) => s + (w.subscriber_count || 0), 0);

  const planCounts = { starter: 0, pro: 0, clean_slate: 0 };
  data.creators.filter(c => c.stripe_customer_id).forEach(c => {
    if (planCounts[c.plan] !== undefined) planCounts[c.plan]++;
  });
  const mrr = Object.entries(planCounts).reduce((s, [plan, count]) => s + (PLAN_PRICES[plan] || 0) * count, 0);

  const signupsByDay = (() => {
    const days = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ date: key, label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count: data.creators.filter(c => c.created_at && c.created_at.slice(0, 10) === key).length });
    }
    return days;
  })();

  const sparkSignups = signupsByDay.slice(-14).map(d => d.count);
  const sparkLeaks = (() => {
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push(data.leaks.filter(l => l.created_at && l.created_at.slice(0, 10) === key).length);
    }
    return days;
  })();

  const leaksByPlatform = data.leaks.reduce((acc, l) => {
    let p = 'unknown';
    if (l.leaked_url) { try { p = new URL(l.leaked_url).hostname.replace('www.',''); } catch {} }
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  const topPlatforms = Object.entries(leaksByPlatform).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const lastLeak = data.leaks[0];

  return (
    <div style={styles.shell}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22, color: '#A78BFA' }}>⬡</span>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 600, color: '#fff', letterSpacing: '0.02em' }}>Creator Protect</h1>
            <p style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Admin dashboard</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 11, color: '#555' }}>{lastRefresh ? `Updated ${timeSince(lastRefresh.toISOString())}` : ''}</span>
          <button onClick={load} style={styles.refreshBtn} disabled={loading}>{loading ? '↻ Loading…' : '↻ Refresh'}</button>
          <a
            href="/admin/leaks"
            style={{
              background: 'transparent',
              color: '#A78BFA',
              border: '1px solid rgba(167,139,250,0.4)',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 12,
              cursor: 'pointer',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            🔍 Leak Triage
          </a>
          <button
            onClick={onSignOut}
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      <div style={styles.body}>
        <CreatorSearch />
        <div style={styles.statGrid}>
          <StatCard label="Total creators" value={totalCreators} sub={`${activeCreators} active · ${trialCreators} trialing`} sparkData={sparkSignups} color="#A78BFA" loading={loading} />
          <StatCard label="Est. MRR" value={`$${mrr.toLocaleString()}`} sub={`${activeCreators} paying subscribers`} color="#34D399" loading={loading} />
          <StatCard label="Leaks detected" value={totalLeaks} sub={`${totalDMCAs} DMCAs filed`} sparkData={sparkLeaks} color="#FB923C" loading={loading} />
          <StatCard label="Videos protected" value={totalVideos} sub={`${data.cohorts.length} cohorts created`} color="#4A9EFF" loading={loading} />
        </div>

        <div style={styles.row2}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>Signups — last 30 days</span>
              <span style={styles.panelBadge}>{data.creators.filter(c => { const d = new Date(); d.setDate(d.getDate() - 30); return new Date(c.created_at) > d; }).length} this month</span>
            </div>
            <BarChart data={signupsByDay} color="#A78BFA" />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              {[0, 7, 14, 21, 29].map(i => <span key={i} style={{ fontSize: 10, color: '#555' }}>{signupsByDay[i]?.label}</span>)}
            </div>
          </div>

          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>Plan distribution</span>
              <span style={styles.panelBadge}>{activeCreators} active</span>
            </div>
            <div style={{ marginTop: 16 }}>
              {Object.entries(planCounts).map(([plan, count]) => {
                const pct = activeCreators > 0 ? Math.round((count / activeCreators) * 100) : 0;
                return (
                  <div key={plan} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 13, color: '#ccc' }}>{PLAN_LABELS[plan]}</span>
                      <span style={{ fontSize: 13, color: '#888' }}>{count} · {pct}%</span>
                    </div>
                    <div style={styles.barTrack}><div style={{ ...styles.barFill, width: `${pct}%`, background: PLAN_COLORS[plan] }} /></div>
                  </div>
                );
              })}
              <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(planCounts).map(([plan, count]) => (
                  <div key={plan} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#888' }}>
                    <span style={{ color: PLAN_COLORS[plan] }}>●</span>
                    <span>{PLAN_LABELS[plan]}</span>
                    <span style={{ marginLeft: 'auto', color: '#888' }}>{count} × ${PLAN_PRICES[plan]} = <b style={{ color: '#fff' }}>${count * PLAN_PRICES[plan]}</b></span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, marginTop: 4 }}>
                  <span style={{ fontWeight: 600, color: '#34D399' }}>Est. MRR</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#34D399' }}>${mrr}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>Recent signups</span>
              <span style={styles.panelBadge}>{totalCreators} total</span>
            </div>
            {loading ? <p style={styles.empty}>Loading…</p> : data.creators.length === 0 ? <p style={styles.empty}>No signups yet</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, fontSize: 12 }}>
                <thead><tr>{['Email','Plan','Stripe','Joined','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.creators.slice(0, 10).map((c, i) => (
                    <CreatorRow key={c.id} creator={c} rowIndex={i} reload={load} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={styles.row2}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>Crawler status</span>
              <span style={{ ...styles.panelBadge, background: lastLeak ? '#16a34a22' : '#71717a22', color: lastLeak ? '#4ade80' : '#a1a1aa' }}>{lastLeak ? '● Active' : '○ No data'}</span>
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Last leak found', value: lastLeak ? fmtDateTime(lastLeak.detected_at) : 'No leaks recorded' },
                { label: 'Total leaks', value: fmt(totalLeaks) },
                { label: 'DMCAs auto-filed', value: fmt(totalDMCAs) },
                { label: 'DMCA rate', value: totalLeaks > 0 ? `${Math.round((totalDMCAs / totalLeaks) * 100)}%` : '—' },
                { label: 'Platforms monitored', value: '19' },
                { label: 'Clean Slate jobs', value: fmt(data.cleanSlate.length) },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>{row.label}</span>
                  <span style={{ fontSize: 13, color: '#ddd', fontWeight: 500 }}>{loading ? '—' : row.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>Top leak platforms</span>
              <span style={styles.panelBadge}>{Object.keys(leaksByPlatform).length} platforms seen</span>
            </div>
            {loading ? <p style={styles.empty}>Loading…</p> : topPlatforms.length === 0 ? <p style={styles.empty}>No leaks recorded yet</p> : (
              <div style={{ marginTop: 16 }}>
                {topPlatforms.map(([platform, count]) => {
                  const pct = totalLeaks > 0 ? Math.round((count / totalLeaks) * 100) : 0;
                  return (
                    <div key={platform} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#ccc' }}>{platform}</span>
                        <span style={{ fontSize: 12, color: '#888' }}>{count} · {pct}%</span>
                      </div>
                      <div style={styles.barTrack}><div style={{ ...styles.barFill, width: `${pct}%`, background: '#FB923C' }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ProspectReportSection />

        <DmcaLetterSection />

        <SerialLeakersSection />

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0 8px', fontSize: 11, color: '#444', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <span>Creator Protect Admin · Tribal Tech</span>
          <span>Supabase live · {loading ? 'syncing…' : 'synced'}</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  shell: { minHeight: '100vh', background: '#080810', color: '#e2e8f0', fontFamily: "'DM Mono', 'Fira Code', 'Courier New', monospace" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: '#0a0a15', position: 'sticky', top: 0, zIndex: 10 },
  refreshBtn: { fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.05)', color: '#aaa', cursor: 'pointer', fontFamily: 'inherit' },
  body: { padding: '24px 28px', maxWidth: 1400, margin: '0 auto' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 },
  statCard: { background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '18px 20px' },
  statLabel: { fontSize: 11, color: '#666', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 },
  statValue: { fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 },
  statSub: { fontSize: 11, color: '#555', marginTop: 5 },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 },
  panel: { background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '18px 20px' },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  panelTitle: { fontSize: 12, fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase' },
  panelBadge: { fontSize: 11, padding: '3px 8px', borderRadius: 20, background: 'rgba(167,139,250,0.12)', color: '#A78BFA' },
  barTrack: { height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3, transition: 'width 0.6s ease' },
  th: { textAlign: 'left', padding: '6px 10px', color: '#555', fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  td: { padding: '9px 10px', color: '#ccc', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  empty: { color: '#555', fontSize: 12, marginTop: 16, textAlign: 'center', padding: '20px 0' },
  gate: { minHeight: '100vh', background: '#080810', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, fontFamily: "'DM Mono', 'Fira Code', monospace" },
  gateInput: { background: '#0f0f1e', border: '1px solid', borderRadius: 8, padding: '10px 16px', fontSize: 14, color: '#fff', width: 260, outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s' },
  gateBtn: { background: '#A78BFA', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 32px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: 260, fontFamily: 'inherit' },
};

export default function Admin() {
  const [authed, setAuthed] = useState(sessionStorage.getItem('cp_admin_auth') === '1');
  if (!authed) return <PasswordGate onAuth={() => setAuthed(true)} />;
  function signOut() {
    sessionStorage.removeItem('cp_admin_auth');
    sessionStorage.removeItem('cp_admin_key');
    setAuthed(false);
  }
  return <AdminDashboard onSignOut={signOut} />;
}


function ProspectReportSection() {
  const [username, setUsername] = useState('');
  const [generatedBy, setGeneratedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => { loadRecent(); }, []);
  async function loadRecent() {
    try {
      const r = await axios.get(`${BACKEND_API}/api/admin/reports?limit=10`, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
      });
      setRecent(r.data.reports || []);
    } catch {}
  }

  async function generate() {
    if (!username.trim()) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const r = await axios.post(`${BACKEND_API}/api/admin/reports/generate`, {
        username: username.trim(),
        generated_by: generatedBy.trim() || null,
      }, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
      });
      setResult(r.data);
      loadRecent();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
    setBusy(false);
  }

  function copy(url) {
    navigator.clipboard.writeText(url).then(() => {}, () => {});
  }

  return (
    <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: '#fff', letterSpacing: '0.02em', margin: 0, marginBottom: 12 }}>
        Prospect reports
        <span style={{ fontSize: 11, color: '#666', marginLeft: 10, fontWeight: 400 }}>
          generate a shareable /reports/:token link to pitch a creator
        </span>
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr auto', gap: 8, marginBottom: 14 }}>
        <input type="text" placeholder="Target username (e.g. MissVsp)"
               value={username} onChange={e => setUsername(e.target.value)}
               style={{ background:'#1a1a2e', color:'#fff', border:'1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding:'7px 10px', fontSize: 12 }} />
        <input type="text" placeholder="Generated by (optional — e.g. sales-outreach)"
               value={generatedBy} onChange={e => setGeneratedBy(e.target.value)}
               style={{ background:'#1a1a2e', color:'#fff', border:'1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding:'7px 10px', fontSize: 12 }} />
        <button onClick={generate} disabled={busy || !username.trim()}
                style={{ background:'#7c3aed', color:'#fff', border:'none', borderRadius: 4, padding:'7px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Scanning…' : 'Generate report'}
        </button>
      </div>

      {err && <div style={{ color:'#f87171', fontSize: 12, marginBottom: 8 }}>{err}</div>}

      {result && (
        <div style={{ padding: 12, background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.3)', borderRadius: 6, marginBottom: 14 }}>
          <div style={{ color:'#4ade80', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            ✓ Report ready — {result.total_hits} leak{result.total_hits === 1 ? '' : 's'} found
          </div>
          <div style={{ display:'flex', gap: 8, alignItems:'center' }}>
            <code style={{ fontSize: 11, color:'#a78bfa', background:'#0a0a0a', padding:'4px 8px', borderRadius: 3, wordBreak:'break-all' }}>
              {result.public_url}
            </code>
            <button onClick={() => copy(result.public_url)}
                    style={{ padding:'4px 10px', fontSize: 11, background:'transparent', border:'1px solid #333', color:'#94a3b8', borderRadius: 3, cursor:'pointer' }}>
              Copy
            </button>
            <a href={result.public_url} target="_blank" rel="noreferrer"
               style={{ padding:'4px 10px', fontSize: 11, background:'transparent', border:'1px solid #333', color:'#a78bfa', borderRadius: 3, textDecoration:'none' }}>
              Open
            </a>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <>
          <div style={{ fontSize: 11, color:'#888', marginBottom: 6 }}>Recent reports</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ color:'#888', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                <th style={{ padding:'6px 4px', fontWeight: 500 }}>Username</th>
                <th style={{ padding:'6px 4px', fontWeight: 500 }}>Hits</th>
                <th style={{ padding:'6px 4px', fontWeight: 500 }}>Views</th>
                <th style={{ padding:'6px 4px', fontWeight: 500 }}>Generated by</th>
                <th style={{ padding:'6px 4px', fontWeight: 500 }}>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recent.map(r => (
                <tr key={r.token} style={{ color:'#fff', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                  <td style={{ padding:'7px 4px' }}>@{r.target_username}</td>
                  <td style={{ padding:'7px 4px', color: r.total_hits > 0 ? '#f87171' : '#4ade80' }}>{r.total_hits}</td>
                  <td style={{ padding:'7px 4px', color:'#94a3b8' }}>{r.view_count || 0}</td>
                  <td style={{ padding:'7px 4px', color:'#94a3b8' }}>{r.generated_by || '—'}</td>
                  <td style={{ padding:'7px 4px', color:'#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td style={{ padding:'7px 4px' }}>
                    <button onClick={() => copy(r.public_url)}
                            style={{ padding:'2px 8px', fontSize: 10, background:'transparent', border:'1px solid #333', color:'#94a3b8', borderRadius: 3, cursor:'pointer' }}>
                      Copy link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}


// ─── DMCA letter generator ────────────────────────────────────
// Live-previewed takedown notice builder. Operator picks a
// creator, fills in the content + infringing URLs, and the
// template below is interpolated in place. Three actions:
//   Copy to Clipboard — navigator.clipboard
//   Download as .txt  — Blob + a[download]
//   Log as Filed      — POST /api/admin/dmca/log-manual, which
//     writes one leak_detections row per URL with filing_method
//     ='manual' and the full letter stored on each row.
//
// Agent info defaults to the Creator Protect DMCA Team per the
// 2026-10-24 filing-signature rule (CLAUDE.md feedback_filing_
// signature_rule_2026_10_24.md): default signer never includes the
// operator's legal name. Vercel env vars (REACT_APP_DMCA_AGENT_*)
// still override. Legal-name-required platforms (X, fapello,
// fappeningbook, similar) are filed via a separate workflow — this
// builder is for the default channel only.

const DMCA_AGENT = {
  name:    process.env.REACT_APP_DMCA_AGENT_NAME    || 'Creator Protect DMCA Team',
  address: process.env.REACT_APP_DMCA_AGENT_ADDRESS || '6595 Roswell Road, Suite G PMB 6778, Atlanta, GA 30328',
  phone:   process.env.REACT_APP_DMCA_AGENT_PHONE   || '+1 (954) 218-2546',
  email:   process.env.REACT_APP_DMCA_AGENT_EMAIL   || '[AGENT EMAIL NOT CONFIGURED]',
};

// Doxx-gate note: the variant list is held server-side only, in the
// backend OPERATOR_LEGAL_NAME env, and consulted via POST
// /api/admin/dmca/check-letter. Keeping the list out of the frontend
// bundle is deliberate — a prior pass hardcoded the variants in this
// file and the three strings ended up co-located in the public JS
// bundle (a passive doxx). The backend endpoint returns only a mode
// enum, never the matched variant string.

function renderDmcaLetter({
  creatorLegalName, creatorStageName, originalSources, contentDescription,
  infringingUrls, dateStr,
}) {
  const sourcesBlock = originalSources.length
    ? originalSources.map(u => `- ${u}`).join('\n')
    : '- [ORIGINAL SOURCE URLS]';
  const infringingBlock = infringingUrls.length
    ? infringingUrls.map(u => `- ${u}`).join('\n')
    : '- [INFRINGING URLS]';

  return `Subject: DMCA Takedown Notice — Unauthorized Distribution of Copyrighted Work

To Whom It May Concern / Designated DMCA Agent:

I am writing to notify you of copyright infringement occurring on your platform and to request immediate removal of the infringing material pursuant to the Digital Millennium Copyright Act, 17 U.S.C. § 512(c).

1. Identification of the Copyrighted Work

The copyrighted work being infringed is original adult content owned by my client, ${creatorLegalName || '[CREATOR LEGAL NAME]'}, professionally known as ${creatorStageName || '[CREATOR STAGE NAME]'}. The work is legitimately published and monetized at:

${sourcesBlock}

The specific work infringed is: ${contentDescription || '[CONTENT TITLE/DESCRIPTION]'}

2. Identification of the Infringing Material

The infringing material is hosted at the following URL(s) on your platform:

${infringingBlock}

Screenshots and evidence of infringement are available upon request.

3. Contact Information

${DMCA_AGENT.name}
Creator Protect, Authorized Agent
${DMCA_AGENT.address}
${DMCA_AGENT.phone}
${DMCA_AGENT.email}
https://creatorprotect.io

4. Good Faith Statement

I have a good faith belief that use of the copyrighted material described above is not authorized by the copyright owner, its agent, or the law.

5. Statement of Accuracy and Authority

I swear, under penalty of perjury, that the information in this notification is accurate and that I am authorized to act on behalf of ${creatorLegalName || '[CREATOR LEGAL NAME]'}, the owner of the exclusive rights being infringed.

6. Signature

/s/ ${DMCA_AGENT.name}
Authorized Agent, Creator Protect
Date: ${dateStr}

Requested Action: Please remove or disable access to the infringing material identified above within the timeframe required by the DMCA safe harbor provisions. Please confirm removal via reply to ${DMCA_AGENT.email}.

Thank you for your prompt attention to this matter.`;
}

function slugifyForFilename(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'unknown';
}

function DmcaLetterSection() {
  const [creators, setCreators] = useState([]);
  const [creatorId, setCreatorId] = useState('');
  const [legalName, setLegalName] = useState('');
  const [stageName, setStageName] = useState('');
  const [originalSourcesText, setOriginalSourcesText] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [infringingUrlsText, setInfringingUrlsText] = useState('');
  const [platform, setPlatform] = useState('');
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10));
  const [logging, setLogging] = useState(false);
  const [flash, setFlash] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_API}/api/admin/creators/list`, {
      headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
    })
      .then(r => setCreators(r.data.creators || []))
      .catch(e => setErr(e.response?.data?.error || e.message));
  }, []);

  // Prefill stage-name when a creator is picked. Legal name stays
  // blank — the operator has to type it in because it's not on
  // file and must be exact for the notice to be valid.
  useEffect(() => {
    const c = creators.find(x => x.id === creatorId);
    if (c) {
      setStageName(c.primary_username);
    }
  }, [creatorId, creators]);

  // Auto-infer platform from the first infringing URL's hostname
  // if the operator hasn't typed one yet.
  useEffect(() => {
    if (platform) return;
    const first = infringingUrlsText.split(/[\n,]+/).map(s => s.trim()).find(Boolean);
    if (!first) return;
    try {
      const host = new URL(first).hostname.replace(/^www\./, '');
      if (host) setPlatform(host);
    } catch {}
  }, [infringingUrlsText, platform]);

  const originalSources = originalSourcesText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
  const infringingUrls  = infringingUrlsText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
  const letter = renderDmcaLetter({
    creatorLegalName: legalName,
    creatorStageName: stageName,
    originalSources,
    contentDescription,
    infringingUrls,
    dateStr,
  });

  const selectedCreator = creators.find(x => x.id === creatorId);
  const canAct = !!creatorId && infringingUrls.length > 0 && !!platform;

  // Server-mediated doxx gate. Starts in 'pending' (fail-closed) and
  // transitions to 'ok' | 'blocked' based on the backend response.
  // Debounced ~400ms on letter/creatorId change so keystroke-driven
  // edits don't spam the endpoint. Any request error → 'blocked'.
  const [letterCheck, setLetterCheck] = useState({ status: 'pending', mode: null });
  const canExportLetter = letterCheck.status === 'ok';
  const letterBlocked   = letterCheck.status === 'blocked';
  const letterPending   = letterCheck.status === 'pending';

  useEffect(() => {
    if (!creatorId || !letter) {
      setLetterCheck({ status: 'pending', mode: null });
      return undefined;
    }
    let cancelled = false;
    setLetterCheck({ status: 'pending', mode: null });
    const timer = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${BACKEND_API}/api/admin/dmca/check-letter`,
          { creator_id: creatorId, letter_text: letter },
          { headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' } }
        );
        if (cancelled) return;
        if (r.data && r.data.ok === true) {
          setLetterCheck({ status: 'ok', mode: r.data.mode || 'ok' });
        } else {
          setLetterCheck({ status: 'blocked', mode: (r.data && r.data.mode) || 'blocked' });
        }
      } catch (_) {
        if (cancelled) return;
        setLetterCheck({ status: 'blocked', mode: 'request_error' });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [letter, creatorId]);

  function copyLetter() {
    if (!canExportLetter) return;
    navigator.clipboard.writeText(letter).then(
      () => { setFlash('Copied letter to clipboard'); setTimeout(() => setFlash(''), 2000); },
      () => { setErr('Clipboard write failed'); }
    );
  }

  function downloadLetter() {
    if (!canExportLetter) return;
    const stageSlug = slugifyForFilename(stageName || selectedCreator?.primary_username || 'creator');
    const platformSlug = slugifyForFilename(platform || 'platform');
    const filename = `DMCA_${stageSlug}_${platformSlug}_${dateStr}.txt`;
    const blob = new Blob([letter], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function logFiled() {
    if (!canAct) return;
    setLogging(true); setErr(''); setFlash('');
    try {
      const r = await axios.post(`${BACKEND_API}/api/admin/dmca/log-manual`, {
        creator_id: creatorId,
        infringing_urls: infringingUrls,
        platform,
        letter_text: letter,
        original_sources: originalSources,
        content_description: contentDescription,
        filed_at: new Date(dateStr + 'T12:00:00Z').toISOString(),
      }, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
      });
      setFlash(`Logged — ${r.data.count} URL${r.data.count === 1 ? '' : 's'} marked as filed`);
      setTimeout(() => setFlash(''), 3500);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
    setLogging(false);
  }

  const inputStyle = { background:'#1a1a2e', color:'#fff', border:'1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding:'7px 10px', fontSize: 12, fontFamily: 'inherit', width: '100%' };
  const btnStyle = (bg) => ({ background: bg, color:'#fff', border:'none', borderRadius: 4, padding:'8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });

  return (
    <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: '#fff', letterSpacing: '0.02em', margin: 0, marginBottom: 12 }}>
        DMCA letter generator
        <span style={{ fontSize: 11, color: '#666', marginLeft: 10, fontWeight: 400 }}>
          build, preview, and log a manual takedown notice
        </span>
      </h2>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 18 }}>
        {/* ─── Form ────────────────────────────────── */}
        <div>
          <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4 }}>Creator</label>
          <select value={creatorId} onChange={e => setCreatorId(e.target.value)} style={inputStyle}>
            <option value="">— select —</option>
            {creators.map(c => (
              <option key={c.id} value={c.id}>{c.display}</option>
            ))}
          </select>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 8, marginTop: 10 }}>
            <div>
              <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4 }}>Creator legal name</label>
              <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Jane Doe" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4 }}>Stage name</label>
              <input type="text" value={stageName} onChange={e => setStageName(e.target.value)} placeholder="@MissVsp" style={inputStyle} />
            </div>
          </div>

          <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4, marginTop: 10 }}>Original source URLs (one per line)</label>
          <textarea value={originalSourcesText} onChange={e => setOriginalSourcesText(e.target.value)} rows={3}
                    placeholder="https://onlyfans.com/yourname&#10;https://fansly.com/yourname"
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }} />

          <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4, marginTop: 10 }}>Content description</label>
          <input type="text" value={contentDescription} onChange={e => setContentDescription(e.target.value)}
                 placeholder="Full video set from Feb 2026" style={inputStyle} />

          <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4, marginTop: 10 }}>Infringing URLs (one per line)</label>
          <textarea value={infringingUrlsText} onChange={e => setInfringingUrlsText(e.target.value)} rows={5}
                    placeholder="https://bunkr.si/a/abcd1234&#10;https://bunkr.si/a/efgh5678"
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }} />

          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap: 8, marginTop: 10 }}>
            <div>
              <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4 }}>Platform name</label>
              <input type="text" value={platform} onChange={e => setPlatform(e.target.value)} placeholder="bunkr.si" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4 }}>Date</label>
              <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ display:'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button onClick={copyLetter} disabled={!canExportLetter}
                    style={{ ...btnStyle('#334155'), opacity: canExportLetter ? 1 : 0.4, cursor: canExportLetter ? 'pointer' : 'not-allowed' }}>
              Copy to Clipboard
            </button>
            <button onClick={downloadLetter} disabled={!canExportLetter}
                    style={{ ...btnStyle('#334155'), opacity: canExportLetter ? 1 : 0.4, cursor: canExportLetter ? 'pointer' : 'not-allowed' }}>
              Download .txt
            </button>
            <button onClick={logFiled} disabled={!canAct || logging}
                    style={{ ...btnStyle('#7c3aed'), opacity: (!canAct || logging) ? 0.5 : 1, cursor: (!canAct || logging) ? 'not-allowed' : 'pointer' }}>
              {logging ? 'Logging…' : 'Log as Filed'}
            </button>
          </div>
          {letterPending && creatorId && letter && (
            <div style={{ color:'#94a3b8', fontSize: 12, marginTop: 8 }}>
              Verifying letter against operator-identity gate…
            </div>
          )}
          {letterBlocked && (
            <div style={{ color:'#fca5a5', fontSize: 12, marginTop: 8, padding: 10, background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.3)', borderRadius: 4 }}>
              ⚠ Operator-identity gate blocked this letter — Copy/Download disabled.
              {letterCheck.mode === 'legal_name_present' && (
                <> Rewrite the letter to use the agency team wording
                   (e.g. "Creator Protect DMCA Team, Authorized Agent").
                   Legal-name-required platforms (X, fapello, fappeningbook) are filed via a separate workflow,
                   not this builder. The gate applies to every creator — client letters included.</>
              )}
              {letterCheck.mode === 'config_missing' && (
                <> Backend OPERATOR_LEGAL_NAME env is unset — fix on the server
                   before this builder can clear a letter.</>
              )}
              {letterCheck.mode === 'request_error' && (
                <> Check request failed (network / auth / timeout). Failing closed.</>
              )}
              {letterCheck.mode === 'bad_request' && (
                <> Request was malformed (missing creator or letter too short).</>
              )}
              {letterCheck.mode === 'server_error' && (
                <> Backend errored while checking. Failing closed.</>
              )}
            </div>
          )}
          {flash && <div style={{ color:'#4ade80', fontSize: 12, marginTop: 8 }}>✓ {flash}</div>}
          {err && <div style={{ color:'#f87171', fontSize: 12, marginTop: 8 }}>{err}</div>}
        </div>

        {/* ─── Live preview ──────────────────────── */}
        <div>
          <label style={{ fontSize: 10, color:'#888', letterSpacing:'0.06em', textTransform:'uppercase', display:'block', marginBottom: 4 }}>Live preview</label>
          <pre style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.07)', borderRadius: 6, padding: 14, fontSize: 11, color:'#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word', height: 640, overflow: 'auto', fontFamily: "'DM Mono', 'Fira Code', 'Courier New', monospace" }}>
            {letter}
          </pre>
        </div>
      </div>
    </div>
  );
}


function SerialLeakersSection() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_API}/api/admin/serial-leakers`, {
      headers: { 'X-Admin-Key': sessionStorage.getItem('cp_admin_key') || '' },
    }).then(r => {
      setRows(r.data.serial_leakers || []);
      setStats(r.data.stats || null);
    }).catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#fff', letterSpacing: '0.02em', margin: 0 }}>
          Serial leakers
        </h2>
        {stats && (
          <span style={{ fontSize: 11, color: '#666' }}>
            {stats.total_serial_leakers} flagged across {stats.total_flags_recorded} total detections
          </span>
        )}
      </div>
      {loading && <div style={{ color: '#666', fontSize: 12 }}>Loading…</div>}
      {err && <div style={{ color: '#f87171', fontSize: 12 }}>{err}</div>}
      {!loading && !err && rows.length === 0 && (
        <div style={{ color: '#555', fontSize: 12, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
          No serial leakers yet. Identifiers appear here once the same hashed buyer is flagged by ≥2 different creators.
        </div>
      )}
      {rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: '#94a3b8', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <th style={{ padding: '8px 6px', fontWeight: 500 }}>Hashed identifier</th>
              <th style={{ padding: '8px 6px', fontWeight: 500 }}>Creators affected</th>
              <th style={{ padding: '8px 6px', fontWeight: 500 }}>Total flags</th>
              <th style={{ padding: '8px 6px', fontWeight: 500 }}>First detected</th>
              <th style={{ padding: '8px 6px', fontWeight: 500 }}>Last detected</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.hashed_identifier} style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                <td style={{ padding: '10px 6px' }}>
                  <code style={{ fontSize: 11, color: '#a78bfa' }}>
                    {r.hashed_identifier.slice(0, 16)}…{r.hashed_identifier.slice(-8)}
                  </code>
                </td>
                <td style={{ padding: '10px 6px' }}>
                  <span style={{
                    background: 'rgba(248,113,113,0.15)', color: '#f87171',
                    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  }}>
                    {r.creators_affected}
                  </span>
                </td>
                <td style={{ padding: '10px 6px' }}>{r.total_flags}</td>
                <td style={{ padding: '10px 6px', color: '#94a3b8' }}>{new Date(r.first_flagged_at).toLocaleDateString()}</td>
                <td style={{ padding: '10px 6px', color: '#94a3b8' }}>{new Date(r.last_flagged_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
